import { useQueryClient } from '@tanstack/react-query';
import { FC } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';

import Button from '@/components/Button';
import { FormLayout } from '@/components/Forms';
import FormField from '@/components/Forms/FormField';
import { Modal } from '@/components/Modal';
import SidePanel, {
  SidePanelBody,
  SidePanelContent,
  SidePanelFooter,
} from '@/components/SidePanel';
import useAlerts from '@/hooks/useAlerts';
import { EventsProjectResource } from '@/types/resources';
import { api } from '@/utils/api';
import { categories } from '@/utils/data/productionEvents';
import { handleFormErrors } from '@/utils/helpers';

import EventsUserTypeEmailBlock from './EventsUserTypeEmailBlock';

export type FormDataT = {
  name: string;
  channel_owner_email: string;
  compilation_id: string;
  participants: {
    Owner: {
      email: string;
    }[];
    Director: {
      email: string;
    }[];
    Producer: {
      email: string;
    }[];
    Scriptwriting: {
      email: string;
    }[];
    Research: {
      email: string;
    }[];
    Props: {
      email: string;
    }[];
    Art: {
      email: string;
    }[];
    'Art Lead': {
      email: string;
    }[];
    Post: {
      email: string;
    }[];
    Video: {
      email: string;
    }[];
    Costumes: {
      email: string;
    }[];
    Thumb: {
      email: string;
    }[];
    'Make Up': {
      email: string;
    }[];
    'Filming Coordination': {
      email: string;
    }[];
  };
};

interface Props {
  onClose: () => void;
}

const TopBox = styled.div`
  background: ${({ theme }) => theme.colors.grayscaleSuperLight};
  padding: 24px 32px;
  margin-left: -32px;
  margin-right: -32px;
  margin-top: -24px;
`;

interface Props {
  project?: EventsProjectResource;
}

const EventsModal: FC<Props> = ({ onClose, project }) => {
  const queryClient = useQueryClient();

  const navigate = useNavigate();

  const { teamId } = useParams();

  const alerts = useAlerts();

  const {
    handleSubmit,
    setError,
    control,
    formState: { isSubmitting },
  } = useForm<FormDataT>({
    defaultValues: {
      name: project?.name ?? '',
      compilation_id: project?.compilationId ?? '',
      participants: {
        Owner: [
          {
            email: project?.channelOwner.email ?? '',
          },
        ],
        ...categories.reduce(
          (acc, categoryGroup) => ({
            ...acc,
            [categoryGroup.category]: project?.participants.filter(
              (participant) => participant.category === categoryGroup.category,
            ).length
              ? project?.participants
                  .filter(
                    (participant) =>
                      participant.category === categoryGroup.category,
                  )
                  .map((participant) => ({
                    email: participant.email,
                  }))
              : [{ email: '' }],
          }),
          {},
        ),
      },
    },
  });

  const onSubmit = handleSubmit(async (data) => {
    try {
      const { id } = await (project
        ? api.put<FormDataT, never>(
            `/v1/production/events-projects/${project.id}`,
            {
              team_id: teamId,
              ...data,
            },
          )
        : api.post<FormDataT, never>(`/v1/production/events-projects`, {
            team_id: teamId,
            ...data,
          }));

      queryClient.invalidateQueries(['production_events_projects'], {
        exact: false,
      });

      onClose();

      alerts.success(
        `Event project ${project ? 'updated' : 'created'} succesfully`,
      );

      if (!project) {
        navigate(`/teams/${teamId}/production/event/${id}`);
      }
    } catch (err) {
      handleFormErrors<FormDataT>(err, setError, alerts, true);
    }
  });

  return (
    <Modal>
      <SidePanel
        heading={`${project ? 'Update' : 'Create'} Event`}
        onClose={onClose}
      >
        <SidePanelBody onSubmit={onSubmit} autoComplete="off">
          <SidePanelContent>
            <TopBox>
              <FormLayout>
                <FormField<FormDataT>
                  name="name"
                  control={control}
                  label="Project name"
                  placeholder="Add name"
                />
                <FormField<FormDataT>
                  name="compilation_id"
                  control={control}
                  label="Compilation ID"
                  placeholder="Add ID"
                />
                <FormField<FormDataT>
                  name="participants.Owner.0.email"
                  control={control}
                  label="Channel Owner Email"
                  placeholder="Add email"
                  suggestionsEndpoint="/v1/users-emails"
                />
              </FormLayout>
            </TopBox>
            <EventsUserTypeEmailBlock
              heading="Director"
              label="Director"
              control={control}
            />
            <EventsUserTypeEmailBlock
              heading="Producer"
              label="Producer"
              control={control}
            />
            <EventsUserTypeEmailBlock
              heading="Scriptwriting"
              label="Scriptwriter"
              control={control}
            />
            <EventsUserTypeEmailBlock
              heading="Research"
              label="Researcher"
              control={control}
            />
            <EventsUserTypeEmailBlock
              heading="Props"
              label="Propser"
              control={control}
            />
            <EventsUserTypeEmailBlock
              heading="Art"
              label="Art Person"
              control={control}
            />
            <EventsUserTypeEmailBlock
              heading="Art Lead"
              label="Art Lead"
              control={control}
            />
            <EventsUserTypeEmailBlock
              heading="Post"
              label="Post Person"
              control={control}
            />
            <EventsUserTypeEmailBlock
              heading="Video"
              label="Operator"
              control={control}
            />
            <EventsUserTypeEmailBlock
              heading="Costumes"
              label="Costumer"
              control={control}
            />
            <EventsUserTypeEmailBlock
              heading="Thumb"
              label="Thumbnailer"
              control={control}
            />
            <EventsUserTypeEmailBlock
              heading="Make Up"
              label="Artist"
              control={control}
            />
            <EventsUserTypeEmailBlock
              heading="Filming Coordination"
              label="Coordinator"
              control={control}
            />
          </SidePanelContent>
          <SidePanelFooter $actions>
            <Button
              disabled={isSubmitting}
              loading={isSubmitting}
              type="submit"
            >
              {project ? 'Update' : 'Create'}
            </Button>
            <Button color="secondary" onClick={onClose} type="button">
              Cancel
            </Button>
          </SidePanelFooter>
        </SidePanelBody>
      </SidePanel>
    </Modal>
  );
};

export default EventsModal;
